// Find My Part GraphQL - Batch product fetcher using Shopify Storefront API
class FindMyPartGraphQL {
  constructor() {
    this.storefrontUrl = '/api/2024-01/graphql.json';
    // Get token from meta tag
    this.accessToken = this._getStorefrontToken();
  }

  /**
   * Get Storefront API access token from meta tag
   * @returns {string|null} Access token
   */
  _getStorefrontToken() {
    const meta = document.querySelector('meta[name="shopify-storefront-api-token"]');
    if (meta) {
      return meta.getAttribute('content');
    }
    console.error('[GraphQL] Storefront API token not found in meta tags');
    return null;
  }

  /**
   * Batch fetch products by IDs using GraphQL
   * @param {Array<string>} productGids - Array of Shopify GIDs (e.g., ["gid://shopify/Product/123", ...])
   * @returns {Promise<Object>} Map of product ID to product data
   */
  async batchFetchProducts(productGids) {
    if (!productGids || productGids.length === 0) {
      return {};
    }

    if (!this.accessToken) {
      console.error('[GraphQL] No access token available');
      return {};
    }

    // GraphQL has a limit, so we'll batch in groups of 250
    const batchSize = 250;
    const batches = [];

    for (let i = 0; i < productGids.length; i += batchSize) {
      batches.push(productGids.slice(i, i + batchSize));
    }

    const allProducts = {};

    for (const batch of batches) {
      const products = await this._fetchBatch(batch);
      Object.assign(allProducts, products);
    }

    return allProducts;
  }

  /**
   * Fetch a single batch of products
   * @param {Array<string>} gids - Array of GIDs for this batch
   * @returns {Promise<Object>} Map of product ID to product data
   */
  async _fetchBatch(gids) {
    // Build GraphQL query with nodes query for multiple products
    const query = `
      query getProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            handle
            title
            vendor
            availableForSale
            tags
            featuredImage {
              url
              altText
              width
              height
            }
            images(first: 2) {
              edges {
                node {
                  url
                  altText
                  width
                  height
                }
              }
            }
            priceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            compareAtPriceRange {
              minVariantPrice {
                amount
                currencyCode
              }
            }
            variants(first: 1) {
              edges {
                node {
                  id
                  price {
                    amount
                    currencyCode
                  }
                  compareAtPrice {
                    amount
                    currencyCode
                  }
                  availableForSale
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(this.storefrontUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({
          query: query,
          variables: { ids: gids }
        })
      });

      if (!response.ok) {
        console.error('[GraphQL] Request failed:', response.status);
        return {};
      }

      const result = await response.json();

      if (result.errors) {
        console.error('[GraphQL] Errors:', result.errors);
        return {};
      }

      if (!result.data || !result.data.nodes) {
        console.error('[GraphQL] No data returned from GraphQL');
        return {};
      }

      // Convert array to map keyed by product ID
      const productsMap = {};
      result.data.nodes.forEach(product => {
        if (product && product.id) {
          const normalized = this._normalizeProduct(product);
          productsMap[product.id] = normalized;
        }
      });

      return productsMap;
    } catch (error) {
      console.error('[GraphQL] Error fetching products:', error);
      return {};
    }
  }

  /**
   * Normalize GraphQL product data to a simpler format
   * @param {Object} product - GraphQL product object
   * @returns {Object} Normalized product data
   */
  _normalizeProduct(product) {
    const variant = product.variants?.edges?.[0]?.node;
    const images = product.images?.edges?.map(edge => edge.node) || [];
    const secondImage = images.length > 1 ? images[1] : null;

    return {
      id: product.id,
      handle: product.handle,
      title: product.title,
      vendor: product.vendor,
      available: product.availableForSale,
      tags: product.tags || [],
      featuredImage: product.featuredImage?.url || null,
      featuredImageAlt: product.featuredImage?.altText || product.title,
      secondImage: secondImage?.url || null,
      secondImageAlt: secondImage?.altText || product.title,
      price: variant?.price?.amount || product.priceRange?.minVariantPrice?.amount || '0',
      priceFormatted: this._formatMoney(
        variant?.price?.amount || product.priceRange?.minVariantPrice?.amount,
        variant?.price?.currencyCode || product.priceRange?.minVariantPrice?.currencyCode
      ),
      compareAtPrice: variant?.compareAtPrice?.amount || product.compareAtPriceRange?.minVariantPrice?.amount || null,
      compareAtPriceFormatted: variant?.compareAtPrice?.amount
        ? this._formatMoney(
            variant.compareAtPrice.amount,
            variant.compareAtPrice.currencyCode
          )
        : null,
      currencyCode: variant?.price?.currencyCode || product.priceRange?.minVariantPrice?.currencyCode || 'USD',
      url: `/products/${product.handle}`
    };
  }

  /**
   * Format money amount
   * @param {string|number} amount - Amount to format
   * @param {string} currencyCode - Currency code (USD, EUR, etc.)
   * @returns {string} Formatted price
   */
  _formatMoney(amount, currencyCode = 'USD') {
    if (!amount) return '$0.00';

    const numAmount = parseFloat(amount);

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(numAmount);
    } catch (e) {
      return `$${numAmount.toFixed(2)}`;
    }
  }
}

// Make available globally
window.FindMyPartGraphQL = FindMyPartGraphQL;
